<template>
    <div class="vehicle-manager">
        <div class="vehicle-header">
            <h4>{{ $t("route_optimization.vehicles") }}</h4>
        </div>

        <TransitionGroup
            name="vehicle-list"
            tag="div"
            class="d-flex flex-column gap-2"
        >
            <div
                v-for="(vehicle, index) in vehicles"
                :id="`vehicle-${index}`"
                :key="index"
                class="vehicle-card"
            >
                <div class="vehicle-card-header">
                    <span>{{ $t("route_optimization.vehicle") }} {{ index + 1 }}</span>
                    <div class="header-actions">
                        <button
                            class="btn-duplicate"
                            :title="$t('common.copy')"
                            @click="duplicateVehicle(index)"
                        >
                            <font-awesome-icon icon="clone" />
                        </button>
                        <button
                            v-if="vehicles.length > 1"
                            class="btn-remove"
                            @click="removeVehicle(index)"
                        >
                            ×
                        </button>
                    </div>
                </div>

                <div class="row g-2 mb-2">
                    <div class="col-12 col-lg-3">
                        <label class="field-label">{{ $t("route_optimization.select_vehicle") }}</label>
                        <VehicleSelectorDropdown
                            :key="`vehicle-selector-${index}-${vehicle.properties.name}`"
                            @on-vehicle-selected="(v) => handleVehicleSelected(v, index)"
                        />
                    </div>
                </div>

                <div class="row g-2 mb-2">
                    <div class="col-12 col-lg-3">
                        <label class="field-label">{{ $t("route_optimization.vehicle_name") }}</label>
                        <input
                            v-model="vehicle.properties.name"
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': showValidation && !vehicle.properties.name }"
                            :placeholder="$t('route_optimization.vehicle_name')"
                        >
                    </div>

                    <div
                        v-if="operationType !== 'road_inspection'"
                        class="col-12 col-lg-3"
                    >
                        <label class="field-label">{{ $t("route_optimization.work_width") }}</label>
                        <input
                            v-model.number="vehicle.properties.work_width"
                            type="number"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': isVehicleInvalid(vehicle) && (vehicle.properties.work_width <= 0 || vehicle.properties.work_width >= 1500) }"
                            step="0.1"
                            min="0"
                        >
                    </div>

                    <div class="col-12 col-lg-3">
                        <label class="field-label">{{ $t("route_optimization.max_speed") }}</label>
                        <input
                            v-model.number="vehicle.properties.max_speed"
                            type="number"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': isVehicleInvalid(vehicle) && (vehicle.properties.max_speed <= 0 || vehicle.properties.max_speed >= 200) }"
                            step="1"
                            min="0"
                        >
                    </div>

                    <div
                        v-if="operationType !== 'road_inspection'"
                        class="col-12 col-lg-3"
                    >
                        <label class="field-label">{{ $t("route_optimization.work_speed") }}</label>
                        <input
                            v-model.number="vehicle.properties.work_speed"
                            type="number"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': isVehicleInvalid(vehicle) && (vehicle.properties.work_speed <= 0 || vehicle.properties.work_speed >= 200) }"
                            step="1"
                            min="0"
                        >
                    </div>
                </div>

                <div class="row g-2">
                    <div class="col-12">
                        <RouteOptimizationCoordinateEditor
                            :model-value="{ x: vehicle.geometry.coordinates[0], y: vehicle.geometry.coordinates[1] }"
                            :title="$t('route_optimization.start_location')"
                            :point-type="1"
                            :show-buttons="false"
                            :skip-defaults="true"
                            :allow-auto-position="!resubmitJobId && !vehicle.properties.locationManuallySet"
                            :class="{ 'invalid-coordinates': showValidation && (!vehicle.geometry.coordinates[0] || !vehicle.geometry.coordinates[1] || vehicle.geometry.coordinates[0] === 0 || vehicle.geometry.coordinates[1] === 0) }"
                            @update:model-value="updateLocation($event, index)"
                            @manual-position-set="setManualPosition(index)"
                        />
                    </div>
                </div>
            </div>
        </TransitionGroup>

        <div
            class="d-grid"
        >
            <button
                class="btn btn-sm btn-outline-primary"
                @click="addVehicle"
            >
                + {{ $t("route_optimization.add_vehicle") }}
            </button>
        </div>
    </div>
</template>

<script>
import RouteOptimizationCoordinateEditor from '@/components/routeoptimization/RouteOptimizationCoordinateEditor.vue'
import VehicleSelectorDropdown from '../vehicle/VehicleSelectorDropdown.vue'
import CoordinateEditor from "@/components/CoordinateEditor.vue"

export default {
    name: "VehicleManager",
    components: {
        VehicleSelectorDropdown,
        RouteOptimizationCoordinateEditor,
        CoordinateEditor
    },
    props: {
        modelValue: {
            type: Array,
            default: () => []
        },
        showValidation: {
            type: Boolean,
            default: false
        },
        selectedGeometry: {
            type: Object,
            default: null
        },
        operationType: {
            type: String,
            default: 'winter_maintenance'
        }
    },
    emits: ['update:modelValue'],
    data() {
        return {
            vehicles: this.modelValue.length > 0 ? [...this.modelValue] : [this.createDefaultVehicle()]
        };
    },
    computed: {
        resubmitJobId() {
            return this.$store.state.routeOptimization.resubmitJobId;
        },
        isVehicleInvalid() {
            return (vehicle) => {
                if (!this.showValidation) return false
                const base = !vehicle.properties.max_speed ||
                             vehicle.properties.max_speed <= 0 ||
                             vehicle.properties.max_speed >= 200 ||
                             !vehicle.geometry.coordinates[0] ||
                             !vehicle.geometry.coordinates[1] ||
                             vehicle.geometry.coordinates[0] === 0 ||
                             vehicle.geometry.coordinates[1] === 0
                if (this.operationType === 'road_inspection') return base
                return base ||
                       !vehicle.properties.work_width  || vehicle.properties.work_width  <= 0 || vehicle.properties.work_width  >= 1500 ||
                       !vehicle.properties.work_speed  || vehicle.properties.work_speed  <= 0 || vehicle.properties.work_speed  >= 200
            }
        }
    },
    watch: {
        modelValue: {
            handler(newVal) {
                const normalized = Array.isArray(newVal) ? [...newVal] : [];
                const nextVehicles = normalized.length > 0 ? normalized : [this.createDefaultVehicle()];

                const isEqual =
                    this.vehicles.length === nextVehicles.length &&
                    this.vehicles.every((item, index) => item === nextVehicles[index]);

                if (!isEqual) {
                    this.vehicles = nextVehicles;
                }
            },
            deep: true,
        },
        vehicles: {
            handler(newVal) {
                this.$emit('update:modelValue', newVal);
            },
            deep: true
        },
        selectedGeometry: {
            handler(newGeometry) {
                const routeStart = this.getRouteStartPoint();
                if (routeStart) {
                    this.vehicles.forEach(vehicle => {
                        if (!vehicle.properties.locationManuallySet) {
                            vehicle.geometry.coordinates = [...routeStart];
                        }
                    });
                }
            },
            deep: true
        }
    },
    methods: {
        createDefaultVehicle() {
            const routeStart = this.getRouteStartPoint();
            return {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: routeStart || [25.7482, 62.2426] },
                properties: { name: '', work_width: 0, max_speed: 0, work_speed: 0, type: 'vehicle' }
            };
        },
        getRouteStartPoint() {
            if (!this.selectedGeometry?.features?.length) return null;

            const firstFeature = this.selectedGeometry.features[0];
            if (firstFeature?.geometry?.coordinates?.length) {
                const coords = firstFeature.geometry.coordinates;
                if (firstFeature.geometry.type === 'LineString') {
                    return [coords[0][0], coords[0][1]];
                } else if (firstFeature.geometry.type === 'MultiLineString' && coords[0]?.length) {
                    return [coords[0][0][0], coords[0][0][1]];
                }
            }
            return null;
        },
        addVehicle() {
            this.vehicles.push(this.createDefaultVehicle());
            const lastVehicleIndex = this.vehicles.length - 1;

            this.$nextTick(() => {
                const vehicleCardElement = document.getElementById(`vehicle-${lastVehicleIndex}`);
                vehicleCardElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        },
        duplicateVehicle(index) {
            const vehicleToDuplicate = this.vehicles[index];
            const duplicatedVehicle = JSON.parse(JSON.stringify(vehicleToDuplicate));
            // Remove locationManuallySet flag from duplicated vehicle
            delete duplicatedVehicle.properties.locationManuallySet;
            this.vehicles.push(duplicatedVehicle);
            const lastVehicleIndex = this.vehicles.length - 1;

            this.$nextTick(() => {
                const vehicleCardElement = document.getElementById(`vehicle-${lastVehicleIndex}`);
                vehicleCardElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        },
        removeVehicle(index) {
            this.vehicles.splice(index, 1);
        },
        handleVehicleSelected(vehicle, index) {
            if (vehicle) {
                const currentVehicle = this.vehicles[index];

                if (vehicle.make) currentVehicle.properties.name = vehicle.make;
                if (vehicle.vehicle_model) currentVehicle.properties.name = `${currentVehicle.properties.name} ${vehicle.vehicle_model}`;
                if (vehicle.working_width) currentVehicle.properties.work_width = vehicle.working_width;
                if (vehicle.max_speed) currentVehicle.properties.max_speed = vehicle.max_speed;
                if (vehicle.working_speed) currentVehicle.properties.work_speed = vehicle.working_speed;
                if (vehicle.home_location) {
                    if (vehicle.home_location.coordinates) {
                        currentVehicle.geometry.coordinates[0] = vehicle.home_location.coordinates[0];
                        currentVehicle.geometry.coordinates[1] = vehicle.home_location.coordinates[1];
                    } else if (vehicle.home_location.x != null && vehicle.home_location.y != null) {
                        currentVehicle.geometry.coordinates[0] = vehicle.home_location.x;
                        currentVehicle.geometry.coordinates[1] = vehicle.home_location.y;
                    }
                }
            }
        },
        updateLocation(location, index) {
            this.vehicles[index].geometry.coordinates = [location.x, location.y];
        },
        setManualPosition(index) {
            this.vehicles[index].properties.locationManuallySet = true;
        }
    }
};
</script>

<style scoped>
.vehicle-manager {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.vehicle-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.vehicle-header h4 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
}

.vehicle-card {
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
}

.vehicle-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    font-weight: 500;
    color: #555;
}

.header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
}

.btn-duplicate {
    background: none;
    border: none;
    color: #007bff;
    font-size: 1rem;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background-color 0.2s;
}

.btn-duplicate:hover {
    background-color: #e7f3ff;
}

.btn-remove {
    background: none;
    border: none;
    color: #dc3545;
    font-size: 1.5rem;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    width: 24px;
    height: 24px;
}

.btn-remove:hover {
    color: #c82333;
}

.field-label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    font-weight: 500;
    color: #555;
}

.is-invalid {
    border-color: #dc3545 !important;
}

.invalid-coordinates :deep(input) {
    border-color: #dc3545 !important;
}

.vehicle-list-move,
.vehicle-list-enter-active {
    transition: all 0.5s ease;
}
.vehicle-list-leave-active {
    transition: all 0.1s ease;
}
.vehicle-list-enter-from,
.vehicle-list-leave-to {
    opacity: 0;
    transform: translateX(0px);
}

.vehicle-list-leave-active {
    position: absolute;
}
</style>
